import React, { useState, useEffect } from 'react';
import { 
  FiSearch, FiRefreshCw, FiFilter, FiCalendar,
  FiAlertCircle, FiInfo, FiCheckCircle, FiXCircle,
  FiChevronDown, FiChevronUp, FiDownload
} from 'react-icons/fi';
import { api } from '../../services/api';
import { reportPlatformLog } from '../../utils/platformLogger';

const SERVICE_LABELS = {
  'backend-main': 'Backend API',
  'web-frontend': 'Admin web',
  'security_activity_log': 'Sécurité',
  users: 'Comptes (historique)',
};

const getServiceLabel = (service) =>
  SERVICE_LABELS[service] || service || '—';

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// Obtenir la couleur en fonction du niveau
const getLevelColor = (level) => {
  switch(level) {
    case 'error':
      return 'bg-red-600/20 text-red-400 border border-red-600/50';
    case 'warn':
      return 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50';
    case 'info':
      return 'bg-blue-600/20 text-blue-400 border border-blue-600/50';
    case 'success':
      return 'bg-green-600/20 text-green-400 border border-green-600/50';
    default:
      return 'bg-gray-700 text-gray-300 border border-gray-600';
  }
};

const getLevelIcon = (level) => {
  switch(level) {
    case 'error':
      return <FiXCircle className="w-4 h-4" />;
    case 'warn':
      return <FiAlertCircle className="w-4 h-4" />;
    case 'info':
      return <FiInfo className="w-4 h-4" />;
    case 'success':
      return <FiCheckCircle className="w-4 h-4" />;
    default:
      return <FiInfo className="w-4 h-4" />;
  }
};

function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    loadLogs();
  }, [filterLevel, filterService]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getLogs({
        level: filterLevel === 'all' ? undefined : filterLevel,
        service: filterService === 'all' ? undefined : filterService,
        limit: 2000,
        offset: 0
      });
      
      if (response && response.success) {
        setLogs(response.data || []);
        reportPlatformLog('info', `Logs d’activité chargés (${(response.data || []).length} entrées)`);
      } else {
        throw new Error(response?.message || 'Erreur lors du chargement des logs');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des logs:', err);
      reportPlatformLog('error', `Échec chargement logs admin: ${err.message || err}`);
      setError(err.message || 'Erreur lors du chargement des logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedLogs = [...logs].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (sortField === 'timestamp') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const filteredLogs = sortedLogs.filter(log => {
    const matchesSearch = 
      log.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.ip?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getServiceLabel(log.service).toLowerCase().includes(searchTerm.toLowerCase());
    
    const logLevel = log.level === 'warning' ? 'warn' : log.level;
    const wantedLevel = filterLevel === 'warning' ? 'warn' : filterLevel;
    const matchesLevel = filterLevel === 'all' || logLevel === wantedLevel;
    const matchesService =
      filterService === 'all' ||
      String(log.service || '').toLowerCase() === filterService;
    
    return matchesSearch && matchesLevel && matchesService;
  });

  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);

  const levelCounts = {
    all: logs.length,
    error: logs.filter(l => l.level === 'error').length,
    warn: logs.filter(l => l.level === 'warn').length,
    info: logs.filter(l => l.level === 'info').length,
    success: logs.filter(l => l.level === 'success').length
  };

  const handleExportCSV = () => {
    const headers = ['Niveau', 'Source', 'Message', 'Utilisateur', 'IP', 'Date'];
    const csvContent = [
      headers.join(','),
      ...filteredLogs.map(log => [
        log.level,
        getServiceLabel(log.service),
        `"${log.message}"`,
        log.user || '',
        log.ip || '',
        formatDate(log.timestamp)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `logs_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-4xl font-bold text-yellow-400 mb-2">
            Logs d'activité
          </h1>
          <p className="text-gray-400 font-medium">
            Erreurs, avertissements, infos et succès — backend API et admin web
          </p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-all font-semibold text-gray-300 hover:border-yellow-600/50 flex items-center shadow-sm"
          >
            <FiDownload className="mr-2" />
            Exporter CSV
          </button>
              <button
                onClick={loadLogs}
                className="px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold flex items-center shadow-lg shadow-yellow-500/30"
              >
                <FiRefreshCw className="mr-2" />
                Actualiser
              </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Total</p>
            <FiInfo className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{levelCounts.all}</p>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Erreurs</p>
            <FiXCircle className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{levelCounts.error}</p>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Avertissements</p>
            <FiAlertCircle className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{levelCounts.warn}</p>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Infos</p>
            <FiInfo className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{levelCounts.info}</p>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Succès</p>
            <FiCheckCircle className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{levelCounts.success}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher dans les logs..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500 transition-all"
            />
          </div>
          <div className="flex items-center space-x-3">
            <FiFilter className="text-gray-400" />
            <select
              value={filterLevel}
              onChange={(e) => {
                setFilterLevel(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500"
            >
              <option value="all">Tous les niveaux</option>
              <option value="error">Erreurs</option>
              <option value="warn">Avertissements</option>
              <option value="info">Infos</option>
              <option value="success">Succès</option>
            </select>
            <select
              value={filterService}
              onChange={(e) => {
                setFilterService(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes les sources</option>
              <option value="backend-main">Backend API</option>
              <option value="web-frontend">Admin web</option>
              <option value="security_activity_log">Sécurité</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200/60">
            <thead className="bg-gray-900">
              <tr>
                <th 
                  className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-slate-100/50 transition-colors"
                  onClick={() => handleSort('level')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Niveau</span>
                    <SortIcon field="level" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Message
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  IP
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-slate-100/50 transition-colors"
                  onClick={() => handleSort('timestamp')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Date</span>
                    <SortIcon field="timestamp" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="relative">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-700"></div>
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent border-t-yellow-600 absolute top-0 left-0"></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <FiAlertCircle className="text-red-500 text-4xl mb-2" />
                      <p className="text-red-400 font-semibold">{error}</p>
                      <button
                        onClick={loadLogs}
                        className="mt-4 px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all"
                      >
                        Réessayer
                      </button>
                    </div>
                  </td>
                </tr>
              ) : paginatedLogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center">
                      <FiAlertCircle className="text-gray-500 text-4xl mb-2" />
                      <p>Aucun log trouvé</p>
                      {logs.length === 0 && (
                        <p className="text-sm text-gray-500 mt-2 text-center max-w-md">
                          Les journaux backend et admin web apparaîtront ici après activité (erreurs API, chargement admin, etc.).
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-700/50 transition-all duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1.5 inline-flex items-center text-xs leading-5 font-bold rounded-full shadow-sm ${getLevelColor(log.level)}`}>
                        {getLevelIcon(log.level)}
                        <span className="ml-1.5 capitalize">{log.level}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-700/80 text-gray-200 border border-gray-600">
                        {getServiceLabel(log.service)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-white max-w-md">
                        {log.message}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {log.user || 'Système'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-400 font-mono">
                        {log.ip || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-400 flex items-center">
                        <FiCalendar className="mr-2 text-gray-400" />
                        {formatDate(log.timestamp)}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-900 px-6 py-4 flex items-center justify-between border-t border-gray-700">
            <div className="text-sm text-gray-300 font-medium">
              Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredLogs.length)} sur {filteredLogs.length} logs
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminLogs;

